/**
 * ExpertAvatar - 数字人封装类
 * 集成Gaussian Splatting渲染器
 */

import * as GaussianSplats3D from 'gaussian-splat-renderer-for-lam';
import { Processor } from './Processor.js';
import { EDUGATE_API_URL } from '../../config/serviceConfig.js';
import debugLog from '../../utils/debugLog.js';
import latencyTrace from '../../utils/latencyTrace.js';

// 🌍 全局状态：跟踪是否已触发预加载
let hasPreloadedMentor = false;
let firstInstanceInitialized = false;

// 🌍 全局已预下载过的 zip URL 集合，避免同一 zip 被多个组件实例重复 fetch
// （即使浏览器 HTTP 缓存命中，重复发起仍浪费带宽和事件循环）
const preloadedZipUrls = new Set();
// 同一 url 同时只允许一个 in-flight 下载，并发请求复用同一 Promise
const inflightPreloads = new Map();

/* 预下载并发闸门。
 *
 * 原来是 roster 有几个角色就同时发几个请求。首次进一个新项目时 zip 全是冷的，
 * 14 个 8MB 并发下载会把连接占满 10 到 17 秒，而开场发言的 speak HTTP 正好排在
 * 它们后面——实测开场 TTS 首包被拖到 12.5 秒，其中 9.9 秒纯粹在等 HTTP 响应。
 * 限到 2 路之后总下载时长基本不变（带宽本来就是瓶颈），但任何时刻只有 2 个请求
 * 在队列里，交互请求能很快插进去。
 */
const PRELOAD_MAX_CONCURRENCY = 2;
let preloadActiveCount = 0;
const preloadSlotWaiters = [];
const acquirePreloadSlot = () => {
  if (preloadActiveCount < PRELOAD_MAX_CONCURRENCY) {
    preloadActiveCount += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => preloadSlotWaiters.push(resolve));
};
const releasePreloadSlot = () => {
  const next = preloadSlotWaiters.shift();
  if (next) next(); // 名额直接转交，不还给计数器
  else preloadActiveCount = Math.max(0, preloadActiveCount - 1);
};

// 🔒 SDK getInstance 全局互斥：getInstance 内部多处读 GaussianSplatRenderer._canvas
// （constructor 的 appendChild、new Viewer 的 threejsCanvas 等），跨多个 await。
// 多个实例并发调 loadAvatar 时，后一个把 _canvas 改成自己的 → 前一个 SDK 内部
// 后续读到错的 canvas，导致两实例 viewer 共用一个 canvas，问题复现。
// 用串行互斥保证：每个实例的 _canvas 赋值 → SDK 完整跑完 getInstance 之间不被打断。
let sdkInstanceMutex = Promise.resolve();

/* 仍在后台构建 splat 的实例。
 *
 * SDK 的 getInstance 把最重的一段（PLY 解析、GPU 上传、排序 Worker）挂在
 * `addSplatScene(...).then(...)` 上就直接 return 了，不 await。所以互斥锁一放，
 * 上一个形象其实还在烧 CPU/GPU，下一个 getInstance 撞上去就从 0.4s 劣化到 20s+。
 * 用 loadProgress(1) 这个回调当完成信号，把这些"影子任务"登记在这里，
 * 让后台续 mount 能真正等它们收尾。
 */
const pendingSplatBuilds = new Set();
const SPLAT_BUILD_TIMEOUT_MS = 90_000;

/* 曾经这里有一套「入队即后台预切」（PrewarmTrack）：在 </voice> 闭合时提前建 session
 * 并发一次 speak，指望真正轮到该角色时接管已缓冲的音频。实测放弃：
 *   - 预热从触发到就绪要 ~1.1s（建 session + 开 WS + speak HTTP），而正常路径在消息
 *     入队后立刻就发 speak，预热永远慢一步，接管率为 0
 *   - 代价却是每轮发言让远程 GPU 把同一段文本完整合成两遍（TTS + LAM），
 *     白白占用产出首包的那台机器
 * 现在改为「</voice> 闭合即入队」，直接把 speak 提前，不再需要影子链路。
 */
const DIAG_PREFIX = '[录课数字人诊断]';

/* ───────────────────────────────────────────────────────────────
 * 🔥 P1 核心：ZIP 预热（HTTP 缓存层面）
 *
 * 实测：
 *   - SDK getInstance 的 13–17s 大头是 axios 下载 + jszip 解压；splat scene 加载是
 *     fire-and-forget 的 `.then()`，不在 await 链路上。
 *   - ZIP 预下载到 HTTP 缓存后，相同链路实测 0.95–2.55s（缩短 80%+）。
 *
 * 曾尝试"多 renderer 实例缓存 + 切换激活"方案，但 SDK 内部 `_canvas` 是 static 共享，
 * 多个 THREE.WebGLRenderer attach 到同一 canvas 会触发 WebGL INVALID_OPERATION
 * （per-renderer program/uniform location 缓存失效），画面错乱不可用。已撤回。
 *
 * 当前策略：只做 ZIP HTTP 缓存预热，不做 renderer 实例预热。warmupAvatar = preloadModel。
 * ─────────────────────────────────────────────────────────────── */

/* ───────────────────────────────────────────────────────────────
 * 🚨 关键全局变量：activeProcessor
 *
 * GaussianSplatRenderer.getInstance() 是全局单例，第一次创建后被永久缓存，
 * 它的 getExpressionData 回调闭包到首个 ExpertAvatar 实例的 processor。
 * 当切换数字人（destroy 旧实例 + new 新实例）时：
 *   - 旧 processor 已被置 null → 渲染器拿不到 blendshapes → 嘴和表情不动
 *   - 新 processor 接收 WS 数据但无人消费
 *
 * 解决：渲染器回调改为读取 *模块级* activeProcessor，
 *      每个 ExpertAvatar.startSession 成功后把自己注册为 active。
 *      destroy 时仅当自己是 active 才清空，避免抢走新实例的位置。
 * ─────────────────────────────────────────────────────────────── */
let activeProcessor = null;

/**
 * 共享 canvas 单例性质的提示：
 *   GaussianSplatRenderer._canvas 是 SDK 模块级唯一 DOM canvas + WebGL context，
 *   只能挂在一个 container 里。流程训练有"导师 + 专家"两个 ExpertAvatar 实例并存，
 *   一方的 getInstance 会把 _canvas 从另一方的 container 抢走。
 *
 *   不再维护"全局活跃 viewer"和"入口全局 stop RAF"——那会误杀另一个并存实例（如导师）
 *   的 viewer，导致切回时形象永远显示不出来。
 *
 *   现在的策略：
 *     - loadAvatar 入口只 stop 自己的旧 viewer（this.renderer.viewer，switchExpert 同实例切换）
 *     - speak 之前 ensureCanvasOnMyContainer：如果 _canvas 被对端抢走了，立刻拉回来
 *     - destroy 内只 stop 自己的 viewer，不动其他实例
 */
/**
 * 所有活跃 ExpertAvatar 实例的全局注册表（用 WeakSet 避免泄漏）。
 * speak 入口扫一下，把"非自己"的实例 RAF 停掉，让自己独占 _canvas，
 * 避免多个 viewer 同帧抢 _canvas 造成闪烁。
 * 对端 speak 时再启动它自己的 RAF（_ensureCanvasOnMyContainer 内重启）。
 */
const allLiveAvatars = new Set();

/**
 * 安全地停掉 GaussianSplatRenderer 的自驱 RAF。
 *
 * SDK 的 render() 是 `if (viewer.selfDrivenMode) {...} else { throw }`，
 * 不能简单把 selfDrivenMode 设为 false 来停 —— cancelAnimationFrame 无法拦截
 * 已经被 schedule 但尚未触发的下一帧 RAF，那一帧触发后会调 render() → throw
 * "Cannot start viewer unless it is in self driven mode."
 *
 * 正确做法：保留原 render，把 renderer.render 替换为带 __stopped 检查的 no-op 包装。
 * 这样即使有漏网的 RAF 触发，也只是调用 no-op，不抛错也不再 schedule 下一帧。
 * startRendererRAF 时把 __stopped 清掉再调原 render 即可重启循环。
 */
function stopRendererRAF(renderer) {
  if (!renderer) return;
  try {
    const v = renderer.viewer;
    if (v?.requestFrameId) {
      try { cancelAnimationFrame(v.requestFrameId); } catch (_) {}
      v.requestFrameId = null;
    }
    if (typeof renderer.render === 'function' && !renderer.__origRender) {
      renderer.__origRender = renderer.render.bind(renderer);
      renderer.render = function () {
        if (renderer.__stopped) return;
        return renderer.__origRender();
      };
    }
    renderer.__stopped = true;
    if (v) v.selfDrivenModeRunning = false;
  } catch (e) {
    console.warn('⚠️ 停止 renderer RAF 失败（已忽略）:', e?.message);
  }
}

/**
 * 读取容器上的取景参数。两个变量都由使用方的 CSS 声明，没声明就是"不裁切"，
 * 因此对话框等其他调用方的行为不受影响。
 *   --gc-dh-zoom   取景放大倍数（等价于过去写在 canvas 上的 transform: scale(zoom)）
 *   --gc-dh-shift  人物整体下移量，百分比，相对容器高度
 */
function readFramingVars(container) {
  try {
    const cs = getComputedStyle(container);
    const zoom = parseFloat(cs.getPropertyValue('--gc-dh-zoom'));
    const shiftRaw = cs.getPropertyValue('--gc-dh-shift').trim();
    const shiftPct = shiftRaw.endsWith('%') ? parseFloat(shiftRaw) : 0;
    return {
      zoom: Number.isFinite(zoom) && zoom > 1 ? zoom : 1,
      shift: Number.isFinite(shiftPct) ? shiftPct / 100 : 0,
    };
  } catch (_) {
    return { zoom: 1, shift: 0 };
  }
}

/**
 * 用相机取景代替 CSS 放大。
 *
 * setViewOffset 让相机只渲染"放大 zoom 倍的虚拟视口"里与画布等大的那块子矩形，
 * 几何结果与 transform: scale(zoom) translateY(shift) 逐像素等价，但渲染仍按画布
 * 自身的 w×h×DPR 进行 —— 没有放大插值，小尺寸舞台（如综合模式 156px 卡片）下
 * 脸部细节不再被拉花。
 *
 * 高斯点直径会自动跟上：SDK 的 updateSplatMesh 用 camera.projectionMatrix
 * 的 elements[0]/elements[5] 算 focalLength，setViewOffset 恰好把这两个元素乘以
 * zoom，点径与画面同步放大，不需要额外补偿。
 */
function applyCameraFraming(container, viewer, w, h) {
  const cam = viewer?.camera;
  if (!cam || !cam.isPerspectiveCamera || typeof cam.setViewOffset !== 'function') return;
  const { zoom, shift } = readFramingVars(container);
  if (zoom <= 1) {
    if (cam.view?.enabled) cam.clearViewOffset();
    return;
  }
  const fullW = w * zoom;
  const fullH = h * zoom;
  // 屏幕上内容下移 h*shift ⟺ 取景窗口在虚拟视口里上移同样的像素数
  cam.setViewOffset(fullW, fullH, (fullW - w) / 2, (fullH - h) / 2 - h * shift, w, h);
}

/**
 * 把 SDK 内部的 WebGLRenderer drawingBuffer / 像素密度 / 相机投影矩阵同步到
 * 当前 container 尺寸，并重新套用取景。
 *
 * 不要直接改 canvas.width/height —— 那只改 DOM 属性，不会更新 THREE 内部状态，
 * 会出现"画面变大且偏移"。
 *
 * pixelRatio 每次都重读：Viewer 只在构造时取过一次 window.devicePixelRatio，
 * 用户改浏览器缩放或把窗口拖到另一块缩放比不同的显示器后，SDK 自己的
 * ResizeObserver 只会重算 CSS 尺寸，密度停在旧值 → 画面一直偏糊直到实例重建。
 * viewer.devicePixelRatio 与 renderer 的 pixelRatio 必须一起改：前者还参与
 * 高斯点 focalLength 和 viewport 的计算，只改一个会让点径和画面对不上。
 */
function syncRendererViewport(container, renderer) {
  try {
    const v = renderer?.viewer;
    const r = v?.renderer;
    if (!container || !v || !r || typeof r.setSize !== 'function') return;
    const w = container.offsetWidth || container.clientWidth;
    const h = container.offsetHeight || container.clientHeight;
    if (!(w > 0) || !(h > 0)) return;

    const dpr = window.devicePixelRatio || 1;
    if (v.devicePixelRatio !== dpr && !v.ignoreDevicePixelRatio) {
      v.devicePixelRatio = dpr;
      if (typeof r.setPixelRatio === 'function') r.setPixelRatio(dpr);
    }

    r.setSize(w, h);
    if (v.camera && v.camera.isPerspectiveCamera) {
      v.camera.aspect = w / h;
      v.camera.updateProjectionMatrix();
    }
    applyCameraFraming(container, v, w, h);
    if (typeof v.forceRenderNextFrame === 'function') v.forceRenderNextFrame();
  } catch (e) {
    console.warn('⚠️ 同步渲染尺寸/取景失败（已忽略）:', e?.message);
  }
}

/**
 * 确保实例独占的 canvas 挂在自己 container 内、可见，并同步渲染尺寸与取景。
 */
function ensureOwnedCanvas(container, renderer, ownedCanvas) {
  try {
    if (!ownedCanvas || !container) return;
    if (ownedCanvas.parentElement !== container) {
      container.appendChild(ownedCanvas);
    }
    if (ownedCanvas.style.visibility === 'hidden') {
      ownedCanvas.style.visibility = 'visible';
    }
    syncRendererViewport(container, renderer);
  } catch (_) {}
}

function startRendererRAF(renderer) {
  if (!renderer) return;
  try {
    renderer.__stopped = false;
    const v = renderer.viewer;
    if (!v || typeof renderer.render !== 'function') return;
    // ⚠️ 不能用 `!v.requestFrameId` 判断是否已在运行：
    // requestFrameId 字段在上次 RAF 触发时被赋过值，stopRendererRAF 之后
    // 我们没把它清回 null（且即便清了，被 wrapper return 的那帧也不会清）。
    // 这里强制清掉 + 直接调 render() —— SDK 的 render 内部会重新 schedule 下一帧。
    // wrapper 已保证：__stopped=true 时直接 return 不递归，不会双 RAF。
    v.requestFrameId = null;
    v.selfDrivenModeRunning = false;
    renderer.render();
  } catch (e) {
    console.warn('⚠️ 启动 renderer RAF 失败（已忽略）:', e?.message);
  }
}

// 兼容旧调用名（loadAvatar/destroy 内之前传 viewer，现在统一传 renderer）
function stopViewerRAF(viewer) {
  if (!viewer) return;
  // 找回 renderer：viewer 是 renderer.viewer 的反查不直接存在，
  // 但调用点都能拿到 renderer，直接改调用点。这里保留兜底：
  try {
    if (viewer.requestFrameId) {
      try { cancelAnimationFrame(viewer.requestFrameId); } catch (_) {}
      viewer.requestFrameId = null;
    }
    viewer.selfDrivenModeRunning = false;
    // ⚠️ 不再设 selfDrivenMode = false，否则 SDK render() 会 throw
  } catch (_) {}
}

const stripMarkdownForSpeech = (value) => {
  const text = String(value || "");
  return text
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```[\w-]*\n?|```/g, " "))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+[.)]\s+/gm, "")
    .replace(/[*_~>#`|[\]{}\\]/g, "")
    .replace(/-{3,}|={3,}/g, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "和")
    .replace(/&lt;/g, "小于")
    .replace(/&gt;/g, "大于")
    .replace(/\s+/g, " ")
    .trim();
};

export class ExpertAvatar {
  constructor(containerElement, serverUrl) {
    this.container = containerElement;
    this.serverUrl = serverUrl;
    this.sessionId = null;
    this.ws = null;
    this.processor = new Processor();
    this.renderer = null;
    this.loadProgress = 0;
    this.downloadProgress = 0;

    // 🔑 每个实例独占一块 canvas，绕开 SDK 的全局 _canvas 共享。
    // 根因：SDK getInstance 内部 `new Viewer({ threejsCanvas: GaussianSplatRenderer._canvas })`，
    //   不同实例的 WebGLRenderer 全部绑到同一物理 canvas → 后建的覆盖前建的 GL state，
    //   切回旧实例时 RAF 在跑但渲染 state 已被夺走，画面停在对端最后一帧。
    // 修复：每次 loadAvatar 之前把 _canvas 替换为本实例独占的 canvas，
    //   Viewer/WebGLRenderer 持有 canvas 引用，之后即便 _canvas 又被改动也互不影响。
    this.ownedCanvas = document.createElement('canvas');
    this.ownedCanvas.style.position = 'absolute';
    this.ownedCanvas.style.inset = '0';
    this.ownedCanvas.style.width = '100%';
    this.ownedCanvas.style.height = '100%';

    // 容器尺寸变化时重算渲染尺寸和取景。
    // SDK 自带的 ResizeObserver 只调 setSize，不知道 setViewOffset 的存在，
    // 而 view offset 是按像素记的，容器一变（换布局模式、窗口缩放、16vh 卡片高度变化）
    // 旧的取景窗口就和新画布对不上了，必须由我们这一层重新套一次。
    // ⚠️ 只同步尺寸/取景，绝不碰 visibility —— 非当前发言者的 canvas 是 hidden 的，
    // 这里若走 ensureOwnedCanvas 会把满屏 12 个角色一起点亮。
    this.resizeObserver = null;
    this.resizeRafId = 0;
    if (typeof ResizeObserver !== 'undefined' && containerElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeRafId) return;
        this.resizeRafId = requestAnimationFrame(() => {
          this.resizeRafId = 0;
          if (this.renderer) syncRendererViewport(this.container, this.renderer);
        });
      });
      try { this.resizeObserver.observe(containerElement); } catch (_) {}
    }

    allLiveAvatars.add(this);
  }
  
  /**
   * 启动会话
   * @param {string} expertId - 专家ID
   * @param {string} avatarPath - 数字人ZIP文件路径
   * @param {object} expertConfig - 专家配置 {voice, model_name}
   * @param {object} options - 可选配置 {reuseProcessor: boolean}
   */
  async startSession(expertId, avatarPath, expertConfig, options = {}) {
    const { reuseProcessor = false } = options;
    
    // 🔒 防止并发调用
    if (this.isStarting) {
      console.warn('⚠️ startSession 正在执行中，跳过重复调用');
      return;
    }
    
    try {
      this.isStarting = true;
      console.log(`🎬 startSession 被调用: expertId=${expertId}, avatarPath=${avatarPath}, reuseProcessor=${reuseProcessor}`);
      console.log(`📋 expertConfig:`, expertConfig);
      
      // 保存完整配置（用于重连）
      this.expertId = expertId;
      this.avatarPath = avatarPath;
      this.expertConfig = expertConfig;
      
      // 0. Processor 管理：复用或重建
      if (!reuseProcessor) {
        // 完全重建：销毁旧的，创建新的
      if (this.processor) {
        console.log('🔄 清理旧的 Processor');
        this.processor.clear();
      }
      this.processor = new Processor();
      console.log('✨ 创建新的 Processor');
      } else {
        // 复用模式：只清空数据，不替换实例
        if (this.processor) {
          this.processor.clear();
          console.log('♻️ 清空 Processor 数据（复用实例）');
        } else {
          // 异常情况：应该有 processor 但没有
          this.processor = new Processor();
          console.warn('⚠️ 复用模式下 processor 不存在，创建新实例');
        }
      }
      
      const sessionUser = expertConfig.session_user || {};

      // 1. 创建会话，传递专家配置和当前用户信息
      const sessionPayload = {
        expert_id: expertId,
        client_id: `web_client_${Date.now()}`,
        user_id: sessionUser.user_id,
        username: sessionUser.username,
        display_name: sessionUser.display_name,
        user_type: sessionUser.user_type,
        expert_config: {
          voice: expertConfig.voice,
          model_name: expertConfig.model_name || 'cosyvoice-v1'
        }
      };
      console.log(`${DIAG_PREFIX} session创建请求`, {
        expertId,
        avatarPath,
        payload: sessionPayload,
      });
      const response = await fetch(`${this.serverUrl}/api/v1/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionPayload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      this.sessionId = data.session_id;
      console.log(`${DIAG_PREFIX} session创建响应`, {
        expertId,
        sessionId: this.sessionId,
        response: data,
      });
      
      console.log(`✅ 会话创建成功: ${this.sessionId}`);
      
      // 2. 连接WebSocket
      await this.connectWebSocket();
      
      debugLog.emit('SESSION', `✅ 创建会话: ${expertId} sid=${this.sessionId}`);

      // 3. 加载数字人模型
      await this.loadAvatar(avatarPath);

      // 🔑 关键：把当前 processor 注册为模块级 active，让单例渲染器从这里取动画数据
      activeProcessor = this.processor;
      console.log('🎯 已注册为活跃 processor，渲染器将从此实例获取嘴部/表情数据');
      debugLog.emit('RENDER', `🎯 active processor 注册：${expertId}`);

      // 🪄 共享 canvas 重挂载：单例 getInstance 在 instance 已存在时不会重新执行构造函数，
      // 而构造函数内部会执行 _container.appendChild(_canvas)。React 卸载老组件后
      // canvas 仍挂在已分离的旧 DOM 节点上，新容器是空的 → 视觉看不到数字人或显示在错位置。
      try {
        ensureOwnedCanvas(this.container, this.renderer, this.ownedCanvas);
      } catch (canvasErr) {
        console.warn('⚠️ canvas 重挂载失败（已忽略）:', canvasErr.message);
      }

      console.log('🎉 ExpertAvatar初始化完成');
      
      // 🚀 首次初始化完成后，20秒后预加载导师数字人资源
      if (!firstInstanceInitialized) {
        firstInstanceInitialized = true;
        console.log('✅ 检测到首个数字人实例初始化完成，20秒后预加载导师资源');
        
        setTimeout(() => {
          this.preloadMentorAvatar(); // 导师不属于任何流程，不传 workflowName
        }, 20000); // 20秒后执行
      }
      
    } catch (error) {
      console.error('❌ 启动会话失败:', error);
      debugLog.emit('SESSION', `❌ 启动失败: ${error.message}`, 'error');
      throw error;
    } finally {
      this.isStarting = false; // 解锁
    }
  }

  /**
   * 🚀 切换到新角色（不卸载渲染器）
   *
   * 关键优化：
   *   - 同 zip：跳过 loadAvatar（GPU 上的 mesh 完全复用），仅重建 session+WS+processor，
   *     通常 < 1 秒，无视觉中断
   *   - 不同 zip：需要走完整 loadAvatar，由 onLoadingChange 通知上层显示过渡卡
   *
   * @param {string} newExpertId
   * @param {string} newAvatarPath  完整 URL（与 startSession 入参格式一致）
   * @param {object} newExpertConfig
   * @param {(loading: boolean, reason?: string) => void} [onLoadingChange]
   */
  async switchExpert(newExpertId, newAvatarPath, newExpertConfig, onLoadingChange) {
    if (this.isStarting) {
      console.warn('⚠️ 正在启动中，switchExpert 跳过');
      return;
    }

    const sameAvatar = this.avatarPath === newAvatarPath && !!this.renderer;
    debugLog.emit('SPEAKER', `🔀 切换角色：${this.expertId || '∅'} → ${newExpertId} (sameZip=${sameAvatar})`);
    // 上一角色若还有未收尾的发言计时，换人后它已经没有意义，别让它污染下一条
    latencyTrace.discard('speak');

    if (!sameAvatar) {
      onLoadingChange?.(true, '加载新角色资源…');
    }

    try {
      this.isStarting = true;

      // 1. 关闭旧 WebSocket
      if (this.ws) {
        try {
          this.ws.onclose = null; this.ws.onerror = null; this.ws.onmessage = null;
          this.ws.close();
        } catch (_) {}
        this.ws = null;
        debugLog.emit('WS', '🔌 旧 WS 已关闭');
      }

      // 2. 清理 processor 数据并新建（确保 active processor 完全切换）
      if (this.processor) {
        if (activeProcessor === this.processor) activeProcessor = null;
        try { this.processor.clear(); } catch (_) {}
      }
      this.processor = new Processor();

      // 3. 保存新配置
      this.expertId = newExpertId;
      this.avatarPath = newAvatarPath;
      this.expertConfig = newExpertConfig;
      const sessionUser = newExpertConfig.session_user || {};

      // 3.5 ★ 防串台关键时刻：先把新 Processor 注册为 activeProcessor，
      //      之后 connectWebSocket 收到的数据才会流向新 processor
      activeProcessor = this.processor;

      // 4. 建 session + 连 WS
      const switchSessionPayload = {
        expert_id: newExpertId,
        client_id: `web_client_${Date.now()}`,
        user_id: sessionUser.user_id,
        username: sessionUser.username,
        display_name: sessionUser.display_name,
        user_type: sessionUser.user_type,
        expert_config: {
          voice: newExpertConfig.voice,
          model_name: newExpertConfig.model_name || 'cosyvoice-v1'
        }
      };
      console.log(`${DIAG_PREFIX} switch session创建请求`, {
        expertId: newExpertId,
        avatarPath: newAvatarPath,
        payload: switchSessionPayload,
      });
      const response = await fetch(`${this.serverUrl}/api/v1/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(switchSessionPayload)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.sessionId = data.session_id;
      console.log(`${DIAG_PREFIX} switch session创建响应`, {
        expertId: newExpertId,
        sessionId: this.sessionId,
        response: data,
      });
      debugLog.emit('SESSION', `✅ 切换会话: ${newExpertId} sid=${this.sessionId}`);
      await this.connectWebSocket();

      // 6. ★ 关键：loadAvatar 与音频解耦（fire-and-forget）
      //    - session+WS+activeProcessor 已就绪 → switchExpert 立即 resolve
      //    - drainQueue 即可 speak，音频通过 Player 立即播放
      //    - 同时 PNG 占位（在共享 canvas 隐藏期间），等 loadAvatar 完成再露出 3D
      //    - 这样可以把"开口"提前 3-10s（loadAvatar 耗时）
      debugLog.emit('RENDER', `🎯 active processor 注册：${newExpertId}`);

      if (sameAvatar) {
        debugLog.emit('RENDER', '⚡ 同 zip 跳过 loadAvatar（mesh 复用）');
        this.loadAvatarPromise = Promise.resolve();
        // 同 zip 不需要 PNG 占位，立即上报 ready
        onLoadingChange?.(false);
      } else {
        // 防串台：先把本实例的 canvas 隐藏，避免新 blendshapes 喂给旧 splatMesh 时被看见
        try {
          if (this.ownedCanvas) this.ownedCanvas.style.visibility = 'hidden';
        } catch (_) {}

        const expectedExpertId = newExpertId;
        this.loadAvatarPromise = (async () => {
          debugLog.emit('RENDER', `📦 不同 zip → 重新解析: ${newAvatarPath}`);
          try {
            await this.loadAvatar(newAvatarPath);
            // 期间发生再次切换 → 当前角色已不是 expectedExpertId，本次结果作废
            if (this.expertId !== expectedExpertId) {
              debugLog.emit('RENDER', `⏭️ loadAvatar 结果已过期（当前=${this.expertId}）`);
              return;
            }
            ensureOwnedCanvas(this.container, this.renderer, this.ownedCanvas);
            debugLog.emit('SPEAKER', `🎉 3D 形象就绪：${newExpertId}`);
          } catch (err) {
            console.error('❌ loadAvatar 异步失败:', err);
            debugLog.emit('RENDER', `❌ loadAvatar 失败: ${err.message}`, 'error');
          } finally {
            // 只有当前角色仍是 expectedExpertId 时才上报 false
            if (this.expertId === expectedExpertId) {
              onLoadingChange?.(false);
            }
          }
        })();
      }

      debugLog.emit('SPEAKER', `🎉 session 就绪（音频可播）：${newExpertId}`);
    } catch (err) {
      console.error('❌ switchExpert 失败:', err);
      debugLog.emit('SPEAKER', `❌ 切换失败: ${err.message}`, 'error');
      onLoadingChange?.(false);
      throw err;
    } finally {
      this.isStarting = false;
    }
  }

  /**
   * 连接WebSocket
   * 在 HTTPS 页面中自动使用 WSS 协议，避免 Mixed Content 问题
   */
  connectWebSocket() {
    return new Promise((resolve, reject) => {
      // 根据 serverUrl / 当前页面协议自动选择 ws/wss，并保留 serverUrl 的 path 前缀（如 /expert）
      const base = new URL(this.serverUrl, window.location.origin);
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const basePath = base.pathname.replace(/\/$/, '');
      const wsUrl = `${wsProtocol}//${base.host}${basePath}/ws/video_stream/${this.sessionId}`;
      console.log(`${DIAG_PREFIX} WS连接请求`, {
        expertId: this.expertId,
        sessionId: this.sessionId,
        wsUrl,
      });
      
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('🔗 WebSocket已连接');
        console.log(`${DIAG_PREFIX} WS已连接`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          readyState: this.ws?.readyState,
        });
        resolve();
        
        // 心跳
        setInterval(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send('ping');
          }
        }, 30000);
      };
      
      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
      
      this.ws.onerror = (error) => {
        console.error('❌ WebSocket错误:', error);
        console.error(`${DIAG_PREFIX} WS错误`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          error,
        });
        reject(error);
      };
      
      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket已断开');
        console.warn(`${DIAG_PREFIX} WS已断开`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          code: event?.code,
          reason: event?.reason,
          wasClean: event?.wasClean,
        });
      };
    });
  }
  
  /**
   * 处理WebSocket消息
   */
  handleMessage(data) {
    if (data === 'pong') return;
    
    try {
      const message = JSON.parse(data);
      
      if (message.type === 'audio') {
        console.log(`${DIAG_PREFIX} WS收到audio`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          conversationId: message.conversation_id,
          segmentIndex: message.segment_index,
          totalSegments: message.total_segments,
          duration: message.duration,
          sampleRate: message.sample_rate,
        });
        debugLog.emit('SPEAKER', `📥 audio: ${this.expertId || '∅'} sid=${String(this.sessionId || '').slice(0, 8)} seg=${message.segment_index ?? '?'}`);
        latencyTrace.markSpeak('audio1');
        this.processor.processAudio(message);
      } else if (message.type === 'blendshapes') {
        console.log(`${DIAG_PREFIX} WS收到blendshapes`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          conversationId: message.conversation_id,
          frameCount: message.data?.frames?.length,
          channelCount: message.data?.names?.length,
        });
        debugLog.emit('SPEAKER', `📥 blendshapes: ${this.expertId || '∅'} sid=${String(this.sessionId || '').slice(0, 8)}`);
        latencyTrace.markSpeak('bs1');
        this.processor.processBlendShapes(message);
      } else {
        console.log(`${DIAG_PREFIX} WS收到其他消息`, {
          expertId: this.expertId,
          sessionId: this.sessionId,
          message,
        });
      }
    } catch (error) {
      console.error('❌ 解析消息失败:', error);
    }
  }
  
  /**
   * 加载数字人模型（依赖浏览器 HTTP 缓存：zip 已被 preloadModel 预下载则只需 1–3s）
   * @param {string} avatarPath - ZIP 文件路径或完整 URL
   *
   * ⚠️ 关键：调用 SDK getInstance 前，必须停掉旧 renderer 的自驱 RAF 循环。
   *   SDK 的 `getInstance` 是"伪单例"——`this.instance` 从未赋值，每次都 new 一个
   *   全新的 Viewer + WebGLRenderer + 自递归 RAF（render.ts:269 / line 152217）。
   *   旧 RAF 不会自动停。N 次切换后会有 N 个 render() 循环并发操作共享的 _canvas，
   *   导致：
   *     1. WebGL: INVALID_OPERATION uniformMatrix4fv/uniform1f location not from program
   *     2. 越切越慢（实测 3.3s → 6.8s → 10.4s → 11.7s）
   *     3. GPU 资源持续泄漏
   */
  async loadAvatar(avatarPath) {
    try {
      // 只停掉本实例的旧 renderer（switchExpert 同一实例的旧 renderer）
      // ⚠️ 不全局 stop！流程训练里另一个 ExpertAvatar 实例（如对端的专家/导师）
      //    可能仍在合法运行，全局 stop 会让对端切回时形象消失
      stopRendererRAF(this.renderer);

      console.log(`📦 开始加载数字人: ${avatarPath}`);
      const fullUrl = avatarPath.startsWith('http') ? avatarPath : `${this.serverUrl}${avatarPath}`;
      console.log(`🔗 完整URL: ${fullUrl}`);

      const startTime = performance.now();

      // 🔒 串行互斥：SDK getInstance 内部跨多个 await 读 GaussianSplatRenderer._canvas，
      // 必须保证从"赋值 _canvas"到"getInstance 返回"中间没有别的实例修改 _canvas。
      const prevMutex = sdkInstanceMutex;
      let releaseMutex;
      sdkInstanceMutex = new Promise(res => { releaseMutex = res; });
      await prevMutex;
      const buildStart = performance.now();

      // splat 构建完成信号：SDK 在 addSplatScene 的 then 里回调 loadProgress(1)。
      // 超时兜底，避免加载失败时把后台续 mount 永久卡住。
      // 分离「重新下载 zip」和「解压 + 解析」。
      // 空闲加载实测在 0.18s 和 22s 之间跳，怀疑 preloadModel 塞进 HTTP 缓存的 zip
      // 被挤掉了（一个项目 15 个角色 ≈ 120MB，容易超出每源缓存配额），
      // getInstance 只好重新走一趟网络。这两个数一分开就能证实或排除。
      let dlStart = 0;
      let dlEnd = 0;

      let resolveSplat;
      const splatBuilt = new Promise((res) => { resolveSplat = res; });
      const splatTimer = setTimeout(() => resolveSplat('timeout'), SPLAT_BUILD_TIMEOUT_MS);
      splatBuilt.then(() => {
        clearTimeout(splatTimer);
        pendingSplatBuilds.delete(splatBuilt);
      });
      pendingSplatBuilds.add(splatBuilt);

      try {
        // 🔑 在调用 SDK 之前把全局 _canvas 替换为本实例独占的 canvas。
        // SDK 内部 `new Viewer({ threejsCanvas: GaussianSplatRenderer._canvas })`，
        // 这样新建的 Viewer/WebGLRenderer 绑定的就是 this.ownedCanvas，
        // 与其他 ExpertAvatar 实例的 GL context 完全隔离。
        try {
          if (GaussianSplats3D?.GaussianSplatRenderer) {
            GaussianSplats3D.GaussianSplatRenderer._canvas = this.ownedCanvas;
          }
        } catch (_) {}

        this.renderer = await GaussianSplats3D.GaussianSplatRenderer.getInstance(
          this.container,
          avatarPath,
          {
            backgroundColor: this.expertConfig?.backgroundColor || this.expertConfig?.background || '#1e293b',
            getChatState: () => 'speaking',
          getExpressionData: () => {
            // ⚠️ 必须只读本实例的 processor，不看全局 activeProcessor。
            // activeProcessor 是"全局共享 _canvas"时代的遗留指针，记录"最后 startSession 的实例"，
            // 现在每个实例独立 canvas/viewer，若仍读 activeProcessor，
            // 流程训练里"导师 → 专家 → 导师"切回后导师 viewer 会拿到专家的 processor，
            // 自己的 WS 推帧到 this.processor 但 viewer 不读 → 画面静止只剩声音。
            return this.processor ? this.processor.getArkitFaceFrame() : null;
          },
            downloadProgress: (percent) => {
              this.downloadProgress = percent;
              if (!dlStart) dlStart = performance.now();
              if (percent >= 1 && !dlEnd) dlEnd = performance.now();
              console.log(`⬇️ 下载进度: ${(percent * 100).toFixed(1)}%`);
            },
            loadProgress: (percent) => {
              this.loadProgress = percent;
              console.log(`📥 加载进度: ${(percent * 100).toFixed(1)}%`);
              if (percent >= 1) {
                const splatSec = ((performance.now() - buildStart) / 1000).toFixed(2);
                debugLog.emit('RENDER', `🧩 splat 就绪: ${fullUrl.split('/').pop()} (${splatSec}s)`);
                resolveSplat('done');
              }
            }
          }
        );
      } finally {
        // ⏱️ P3 启动期限速：getInstance 完成后延迟 600ms 再释放互斥锁。
        // 原因：jszip 解压 + GLB 解析 + GPU 上传是 CPU 重活，相邻两个实例若紧贴串行，
        // 主线程会被持续占满 → WebSocket onmessage / Processor 入帧严重延迟，
        // 出现"字幕到了，声音/动画 10s 后才动"或"只有字幕没有声音"。
        // 600ms 让浏览器空转一会儿处理 WS / 渲染。8 角色启动总额外耗时 ~4.8s，可接受。
        setTimeout(() => releaseMutex(), 600);
      }

      // ★ 关闭 splat 渐入扫描动画（默认 1–2s 从下往上淡入）
      // SDK 的 getInstance 在 new Viewer 时没透传 sceneRevealMode，必须直接打到 viewer 上。
      // 后续每帧 updateSplatMesh → updateVisibleRegionFadeDistance(this.sceneRevealMode)
      // 读到 Instant，shader uniform fadeInComplete 强制 1，3D 形象立即完整呈现。
      try {
        if (this.renderer?.viewer && GaussianSplats3D?.SceneRevealMode) {
          this.renderer.viewer.sceneRevealMode = GaussianSplats3D.SceneRevealMode.Instant;
        }
      } catch (_) {}

      // 相机取景要在 SDK 起 RAF 之前就位（addSplatScene 的 then 里会直接 render），
      // 否则头几帧会用未裁切的全景构图，看起来是人物"先远后近"跳一下。
      syncRendererViewport(this.container, this.renderer);

      // 分开报「排队等互斥锁」和「真正解析」：启动期 12 个角色串行，
      // 混在一起看会误以为解析本身越来越慢。
      // 注意这里的「解析」只覆盖 await 链（下载/解压/建 Viewer/解析 GLB），
      // 最重的 splat 构建在返回后才跑，由上面的「🧩 splat 就绪」单独计时。
      const now = performance.now();
      const loadTime = ((now - startTime) / 1000).toFixed(2);
      const buildTime = ((now - buildStart) / 1000).toFixed(2);
      const queueTime = ((buildStart - startTime) / 1000).toFixed(2);
      const dlTime = dlStart ? (((dlEnd || now) - dlStart) / 1000).toFixed(2) : '—';
      console.log(`✅ 数字人模型加载完成 (耗时: ${loadTime}秒)`);
      debugLog.emit(
        'RENDER',
        `📦 解析: ${fullUrl.split('/').pop()} (解析 ${buildTime}s，其中下载 ${dlTime}s + 排队 ${queueTime}s)`
      );
    } catch (error) {
      console.error('❌ 加载数字人失败:', error);
      throw error;
    }
  }

  /**
   * 🔥 启动期预热：当前等价于 preloadModel（只做 zip HTTP 预下载）。
   * 保留独立 API 是因为外层语义上"预热角色"比"预下载文件"更直观，
   * 且未来若 SDK 暴露安全的"已解析 splat 复用"接口可直接在此扩展。
   */
  static async warmupAvatar(avatarPath, serverUrl) {
    return ExpertAvatar.preloadModel(avatarPath, serverUrl);
  }

  /**
   * 等到 3D 形象加载真正安静下来：串行队列排空，且没有实例还在后台构建 splat。
   *
   * 给"后台续 mount"用。只等互斥锁是不够的——锁在 getInstance 返回后 600ms 就放了，
   * 而 splat 构建还要再跑十几秒，下一个形象撞上去就会从 0.4s 劣化到 20s+。
   */
  static async waitForLoadIdle() {
    // sdkInstanceMutex 是队列尾。等到它 resolve 后若尾巴又被换过，
    // 说明这期间又有人排进来了，继续等新的尾巴。splat 同理。
    for (let i = 0; i < 64; i += 1) {
      const tail = sdkInstanceMutex;
      const builds = [...pendingSplatBuilds];
      await Promise.all([tail, ...builds]);
      if (sdkInstanceMutex === tail && pendingSplatBuilds.size === 0) return;
    }
  }

  /**
   * 发送文本让数字人说话
   * @param {string} text - 文本内容
   * @param {string} instructText - 情绪指令（可选）
   * @param {boolean} isRetry - 是否是重试（内部使用）
   */
  setMuted(muted) {
    this.processor?.setMuted?.(!!muted);
  }

  async speak(text, instructText = null, isRetry = false) {
    try {
      // ★ 流程训练里有"导师 + 专家"两个 ExpertAvatar 实例并存，SDK 的 _canvas 只能挂一处。
      //   对端 loadAvatar 时会把 _canvas appendChild 到对端 container，自己的 container 就空了。
      //   开口前主动把 _canvas 拉回自己的 container，确保自己说话时形象可见。
      this._ensureCanvasOnMyContainer();

      const speechText = stripMarkdownForSpeech(text) || String(text || "");

      // ── 1. 确保 WS 连通 ─────────────────────────────────────────────────────
      // CONNECTING（刚切换完，握手还在进行中）→ 等 onopen，不走 reconnect
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        await new Promise((resolve) => {
          const onOpen = () => { this.ws.removeEventListener('open', onOpen); resolve(); };
          const onErr  = () => { this.ws.removeEventListener('error', onErr); resolve(); };
          this.ws.addEventListener('open',  onOpen, { once: true });
          this.ws.addEventListener('error', onErr,  { once: true });
          // 兜底超时
          setTimeout(resolve, 3000);
        });
      }
      // 仍未 OPEN → 真的断了，走 reconnect
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        const wsState = this.ws ? this.ws.readyState : 'null';
        console.warn(`⚠️ [speak] WebSocket 未就绪 (状态=${wsState})，尝试重连...`);
        if (isRetry) throw new Error('WebSocket 重连后仍然失败');
        await this.reconnect();
      }

      // ── 2. 在发 HTTP 前先挂好 playbackDone 监听（避免音频先到、再挂导致超时）──
      const playbackTimeoutMs = Math.min(120000, Math.max(20000, speechText.length * 500 + 10000));
      const playbackDone = this.processor?.waitForPlaybackEnd
        ? this.processor.waitForPlaybackEnd(playbackTimeoutMs)
        : Promise.resolve();

      // ── 3. 发 HTTP speak ─────────────────────────────────────────────────────
      latencyTrace.beginSpeak(`${this.expertId || '∅'} ${speechText.length}字`);
      latencyTrace.markSpeak('req');
      console.log(`📤 [speak] 发送HTTP请求: session=${this.sessionId}, 文本长度=${speechText.length}字`);
      const speakPayload = { text: speechText, instruct_text: instructText, speed: 1.0 };
      console.log(`${DIAG_PREFIX} speak请求`, {
        expertId: this.expertId,
        sessionId: this.sessionId,
        voice: this.expertConfig?.voice,
        modelName: this.expertConfig?.model_name,
        wsReadyState: this.ws?.readyState,
        textLength: speechText.length,
        textPreview: speechText.slice(0, 80),
        payload: speakPayload,
      });
      const response = await fetch(`${this.serverUrl}/api/v1/session/${this.sessionId}/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(speakPayload)
      });

      if (!response.ok) {
        console.error(`❌ [speak] HTTP请求失败: ${response.status}`);
        // 404 = 会话过期。
        // ⚠️ 关键：若 switchExpert 正在进行（isStarting=true），它即将给出新的 sessionId+WS，
        //    千万不能再调 reconnect——否则两条路径同时跑，会出现双重 loadAvatar
        //    （日志：reconnect 之后又看到 16s 的 loadAvatar）。
        //    等 isStarting 落下，复用新 session 直接重试。
        if (response.status === 404 && !isRetry) {
          if (this.isStarting) {
            console.warn('⚠️ 会话过期，但 switchExpert 进行中——等待新 session 后直接重试，不 reconnect');
            await this._waitForNotStarting(15000);
            return await this.speak(text, instructText, true);
          }
          console.warn('⚠️ 会话已过期，重连后重试...');
          await this.reconnect();
          return await this.speak(text, instructText, true);
        }
        // 409 = 服务端正在合成上一句，并发拒绝。等当前播放结束再重试一次
        if (response.status === 409 && !isRetry) {
          console.warn('⚠️ 服务端 busy（409），等待当前播放结束后重试...');
          try { await playbackDone; } catch (_) {}
          // 再等一个小间隔，避免连击
          await new Promise((r) => setTimeout(r, 200));
          return await this.speak(text, instructText, true);
        }
        // 重试后仍然 409 → 静默丢弃，不抛错（避免 UI 红框）
        if (response.status === 409 && isRetry) {
          console.warn('⚠️ 重试后仍 busy，丢弃本次 speak');
          debugLog.emit('SPEAKER', `⚠️ 服务端 busy，丢弃：${speechText.slice(0, 12)}…`, 'warn');
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      latencyTrace.markSpeak('http');
      console.log(`✅ [speak] HTTP响应成功: ${speechText.substring(0, 20)}...`);
      console.log(`${DIAG_PREFIX} speak响应成功`, {
        expertId: this.expertId,
        sessionId: this.sessionId,
        status: response.status,
        statusText: response.statusText,
        voice: this.expertConfig?.voice,
      });

      // ── 4. 等待播放结束 ──────────────────────────────────────────────────────
      await playbackDone;

    } catch (error) {
      console.error('❌ 发送文本失败:', error);
      throw error;
    }
  }
  
  /**
   * 等待 isStarting 标记被清空（switchExpert / startSession 完成）。
   * 用于 speak 在 switchExpert 期间收到 404 时，避免再走 reconnect 引发双重 loadAvatar。
   */
  async _waitForNotStarting(timeoutMs = 15000) {
    if (!this.isStarting) return true;
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (!this.isStarting) { resolve(true); return; }
        if (Date.now() - start > timeoutMs) { resolve(false); return; }
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  /**
   * 重新连接（会话过期时）
   *
   * ⚠️ 不调 startSession（会被 isStarting 锁拦截），直接走最小重连路径：
   *   1. 关闭旧 WS
   *   2. 重新创建 HTTP session
   *   3. 重新连接 WS，并等待其真正 OPEN
   *   4. 重新注册 activeProcessor
   */
  async reconnect() {
    console.log('🔄 开始重新连接...');

    // 1. 关闭旧 WS（不置 null，留到新连接建好后替换）
    if (this.ws) {
      try { this.ws.onclose = null; this.ws.onerror = null; this.ws.close(); } catch (_) {}
      this.ws = null;
    }

    // 2. 清理 processor 数据（保留实例，不重建，避免渲染器丢失绑定）
    if (this.processor) {
      try { this.processor.clear(); } catch (_) {}
    }

    // 3. 重新创建 HTTP session
    const oldSessionId = this.sessionId;
    const sessionUser = this.expertConfig?.session_user || {};
    const resp = await fetch(`${this.serverUrl}/api/v1/session/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expert_id: this.expertId,
        client_id: `web_client_${Date.now()}`,
        user_id: sessionUser.user_id,
        username: sessionUser.username,
        display_name: sessionUser.display_name,
        user_type: sessionUser.user_type,
        expert_config: {
          voice: this.expertConfig?.voice,
          model_name: this.expertConfig?.model_name || 'cosyvoice-v1'
        }
      })
    });
    if (!resp.ok) throw new Error(`重连创建会话失败: HTTP ${resp.status}`);
    const data = await resp.json();
    this.sessionId = data.session_id;

    // 4. 重新连接 WS，等待真正 OPEN
    await this.connectWebSocket();

    // 5. 重新注册 activeProcessor（确保渲染器继续拿到动画数据）
    if (this.processor) {
      activeProcessor = this.processor;
    }

    console.log(`✅ 重连成功: ${oldSessionId} → ${this.sessionId}`);
    debugLog.emit('SESSION', `🔄 重连成功: ${this.expertId} sid=${this.sessionId}`);
  }
  
  /**
   * speak 之前确保自己独占的 canvas 在自己 container 内、可见、RAF 在跑。
   *
   * 自从每个实例独占 canvas 后，对端再也不会抢走本实例的 canvas，
   * 多数情况下本方法是 no-op；只在以下边缘情况下生效：
   *   - 容器尺寸变化但 SDK ResizeObserver 还没触发
   *   - 之前因构造异常 RAF 没起来
   */
  _ensureCanvasOnMyContainer() {
    try {
      if (!this.ownedCanvas || !this.container) return;
      const wasDetached = this.ownedCanvas.parentElement !== this.container;
      ensureOwnedCanvas(this.container, this.renderer, this.ownedCanvas);
      if (wasDetached) {
        debugLog.emit('RENDER', `🪄 canvas 已拉回 container: ${this.expertId || '∅'}`);
      }
      startRendererRAF(this.renderer);
    } catch (e) {
      console.warn('⚠️ _ensureCanvasOnMyContainer 失败（已忽略）:', e?.message);
    }
  }

  /**
   * P3 多数字人共存：切换"是否为当前发言者"。
   *
   * - visible=true：canvas 可见 + 启动 RAF（恢复渲染）
   * - visible=false：canvas 隐藏 + 停止 RAF（CPU/GPU 节省，session/WS 不动）
   *
   * 与 _ensureCanvasOnMyContainer 区别：
   *   - _ensureCanvasOnMyContainer 是 speak 之前的"自动恢复兜底"
   *   - setActive 是上层显式调度（同一时刻只有一个实例 active）
   *
   * 注意：不动 processor/session/WS。speak() 仍可被任意实例调用，
   * 即使 active=false 时调 speak 也能正常出声（只是没画面）；多实例同时 speak
   * 也物理可行（每实例独立 canvas/processor/audio source），由调度层保证唯一性。
   */
  setActive(visible) {
    try {
      if (!this.ownedCanvas) return;
      if (visible) {
        ensureOwnedCanvas(this.container, this.renderer, this.ownedCanvas);
        startRendererRAF(this.renderer);
      } else {
        if (this.ownedCanvas.style.visibility !== 'hidden') {
          this.ownedCanvas.style.visibility = 'hidden';
        }
        stopRendererRAF(this.renderer);
      }
    } catch (e) {
      console.warn('⚠️ setActive 失败（已忽略）:', e?.message);
    }
  }

  /**
   * 立即打断当前发言（保留 session/WS/renderer，不销毁实例）
   * 用于：用户按住说话时，应当立即停掉数字人正在播的音频和动画
   */
  interrupt() {
    if (this.processor) {
      // ⚠️ 顺序很关键：必须先 abandonCurrent 再 clear。
      //  - abandonCurrent: 把当前 conversation_id 加入 abandoned 集 + 开 1.5s 静音窗，
      //    后续 WS 到达的同 id 段、以及静音窗内到达的新 id 段都会被 processAudio/processBlendShapes 丢弃
      //  - 否则 clear 后 currentConversationId=null，新到的剩余段会被判为"新对话"重新 new Player 续播，
      //    表现就是"按按钮没打断、数字人接着讲"
      try { this.processor.abandonCurrent(); } catch (_) {}
      try { this.processor.clear(); } catch (_) {}
    }
    debugLog.emit('SPEAKER', `⏹️ 打断当前发言：${this.expertId || '∅'}`);
  }

  /**
   * 销毁资源
   */
  destroy() {
    console.log('🗑️ 开始销毁 ExpertAvatar...');
    debugLog.emit('SESSION', `🗑️ destroy: ${this.expertId || '∅'}`);
    
    // 1. 先关闭 WebSocket（停止接收新数据）
    if (this.ws) {
      try {
        this.ws.close();
        this.ws = null;
      } catch (error) {
        console.warn('⚠️ WebSocket关闭失败（已忽略）:', error.message);
        this.ws = null;
      }
    }
    
    // 2. 清理 Processor（停止音频播放）
    if (this.processor) {
      console.log('🧹 清理 Processor');
      // 🔑 仅当当前 active processor 就是自己时才解除注册，避免抢走新实例的位置
      if (activeProcessor === this.processor) {
        activeProcessor = null;
        console.log('🎯 已解除 active processor 注册');
      }
      try {
        this.processor.clear();
        this.processor = null;
      } catch (error) {
        console.warn('⚠️ Processor清理失败（已忽略）:', error.message);
        this.processor = null;
      }
    }
    
    // 2.5 停掉尺寸监听
    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect(); } catch (_) {}
      this.resizeObserver = null;
    }
    if (this.resizeRafId) {
      try { cancelAnimationFrame(this.resizeRafId); } catch (_) {}
      this.resizeRafId = 0;
    }

    // 3. 清空容器（先清空DOM，让渲染器失效）
    if (this.container && this.container.nodeType === 1) {
      try {
        console.log('🧽 清空容器内容');
        this.container.innerHTML = '';
      } catch (error) {
        console.warn('⚠️ 清空容器失败（已忽略）:', error.message);
      }
    }
    
    // 4. 清除渲染器引用（不调用dispose，避免removeChild错误）
    if (this.renderer) {
      console.log('🎨 清除渲染器引用（跳过dispose调用）');
      // 只停自己的 renderer（对端实例不动）
      stopRendererRAF(this.renderer);
      // ⚠️ 不调用 this.renderer.dispose()，因为它内部的 removeChild 会报错
      // 容器已经清空，渲染器的DOM元素已经被移除，直接置空引用即可
      this.renderer = null;
    }
    
    allLiveAvatars.delete(this);
    console.log('✅ ExpertAvatar已销毁');
  }
  
  /**
   * 预加载导师数字人资源
   * 导师角色名称固定为 "导师"，workflow_name 为空字符串
   */
  async preloadMentorAvatar() {
    if (hasPreloadedMentor) {
      console.log('⏭️ 导师资源已预加载，跳过');
      return;
    }
    
    try {
      hasPreloadedMentor = true; // 标记为已预加载，避免重复
      console.log('🚀 [预加载] 开始查询导师配置...');
      
      // 导师的角色名称固定为 "导师"
      const mentorRoleName = '导师';
      
      // 直接调用 EduGate API 获取配置（workflow_name 为空字符串）
      const mentorConfig = await this.fetchDigitalHumanConfig(mentorRoleName, '', 'MsgtoHuman');
      
      if (!mentorConfig || !mentorConfig.avatarPath) {
        console.warn('⚠️ [预加载] 无法获取导师数字人配置');
        return;
      }
      
      console.log(`📦 [预加载] 导师: ${mentorRoleName}`);
      console.log(`📦 [预加载] 导师数字人路径: ${mentorConfig.avatarPath}`);
      
      // 静默下载 ZIP 文件到浏览器缓存
      await ExpertAvatar.preloadModel(mentorConfig.avatarPath, this.serverUrl);
      
      console.log('✅ [预加载] 导师资源预加载完成');
      
    } catch (error) {
      console.error('❌ [预加载] 导师资源预加载失败:', error);
      // 失败不影响主流程，静默处理
    }
  }
  
  /**
   * 获取数字人配置（复用 DigitalHumanDialog 的逻辑）
   * @param {string} roleName - 角色名称
   * @param {string} workflowName - 流程名称（导师传空字符串）
   * @param {string} messageType - 消息类型
   * @returns {Promise<object|null>} 数字人配置
   */
  async fetchDigitalHumanConfig(roleName, workflowName, messageType) {
    try {
      console.log('🌐 [预加载] 请求数字人配置:', { roleName, workflowName, messageType });
      
      const response = await fetch(`${EDUGATE_API_URL}/api/digital-human/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_name: roleName,
          workflow_name: workflowName, // 导师传空字符串 ''
          message_type: messageType
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      console.log('📦 [预加载] EduGate 返回:', result);
      
      if (result.code === 200 && result.data) {
        return {
          id: roleName.replace(/\s+/g, '_'),
          name: roleName,
          avatarPath: result.data.avatar_path,
          voice: result.data.avatar_voice,
          model_name: 'cosyvoice-v1'
        };
      }
      
      return null;
    } catch (error) {
      console.error('❌ [预加载] 获取数字人配置失败:', error);
      return null;
    }
  }
  
  /**
   * 静态方法：预加载模型文件到浏览器缓存
   * @param {string} avatarPath - 数字人ZIP文件路径
   * @param {string} serverUrl - 服务器URL
   */
  static async preloadModel(avatarPath, serverUrl) {
    const fullUrl = avatarPath.startsWith('http') ? avatarPath : `${serverUrl}${avatarPath}`;

    // 🔒 已预下载过 → 直接返回（HTTP 缓存兜底，但避免重复网络/事件开销）
    if (preloadedZipUrls.has(fullUrl)) return;
    // 🔒 同一 url 已在下载中 → 复用 Promise，并发调用只会发起一次请求
    if (inflightPreloads.has(fullUrl)) return inflightPreloads.get(fullUrl);

    const task = (async () => {
      const queuedAt = performance.now();
      await acquirePreloadSlot();
      try {
        console.log(`📥 [预加载] 开始下载: ${fullUrl}`);
        debugLog.emit('PRELOAD', `📥 ${fullUrl.split('/').pop()}`);

        const startTime = performance.now();
        const response = await fetch(fullUrl, { method: 'GET', cache: 'force-cache' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await response.blob();

        const loadTime = ((performance.now() - startTime) / 1000).toFixed(2);
        const waitTime = ((startTime - queuedAt) / 1000).toFixed(2);
        console.log(`✅ [预加载] 下载完成，耗时: ${loadTime}秒`);
        debugLog.emit('PRELOAD', `✅ ${loadTime}s ${fullUrl.split('/').pop()}（候 ${waitTime}s）`);
        preloadedZipUrls.add(fullUrl);
      } catch (error) {
        console.error('❌ [预加载] 下载失败:', error);
        throw error;
      } finally {
        releasePreloadSlot();
        inflightPreloads.delete(fullUrl);
      }
    })();

    inflightPreloads.set(fullUrl, task);
    return task;
  }
}

